import { FC, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { COLORS } from 'services/constants/colors';
import { PAYMENT_GATEWAY_HANGFIRE_URL } from 'services/constants/env';
import { isPathAllowed, PATHS } from 'routes/constants';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { selectRealmRoles } from 'redux/reducers/auth/selectors';
import { TabGroupMenu } from 'components/ReferenceTables/TabGroupMenu';
import {
	EPgTab,
	PG_ALL_TAB_VALUES,
	PG_DEFAULT_TAB,
	PG_GROUPED_TAB_VALUES,
	PG_PLAIN_TABS,
	PG_TAB_GROUPS,
	pgTabPath,
} from './constants';
import { Payments } from './Payments';
import { PaymentViewerProvider } from './Payments/PaymentViewer';
import { Customers } from './Customers';
import { Alerts } from './Alerts';
import { Monitoring } from './Monitoring';
import { Audit } from './Audit';
import { System } from './System';
import { Services } from './Services';
import { Fees } from './Fees';
import { DdFx } from './DdFx';
import { DebtRules } from './DebtRules';
import { Schedule } from './Schedule';
import { Accounts } from './Accounts';
import { ClientAccess } from './ClientAccess';

const TAB_CONTENT: Record<EPgTab, FC> = {
	[EPgTab.PAYMENTS]: Payments,
	[EPgTab.CUSTOMERS]: Customers,
	[EPgTab.ALERTS]: Alerts,
	[EPgTab.MONITORING]: Monitoring,
	[EPgTab.AUDIT]: Audit,
	[EPgTab.SYSTEM]: System,
	[EPgTab.SERVICES]: Services,
	[EPgTab.FEES]: Fees,
	[EPgTab.DD_FX]: DdFx,
	[EPgTab.DEBT_RULES]: DebtRules,
	[EPgTab.SCHEDULE]: Schedule,
	[EPgTab.ACCOUNTS]: Accounts,
	[EPgTab.CLIENT_ACCESS]: ClientAccess,
};

const isPgTab = (value: string): value is EPgTab => PG_ALL_TAB_VALUES.includes(value);

/**
 * Finance & VAT → Payment Gateway. The tab lives in the URL (`/payment-gateway/:tab`), as in
 * Reference Tables. The section needs the Keycloak realm role `paymentgateway_admin` in the access
 * token (the CC `admin` role does not grant it): without it the page shows "Access denied" and
 * sends no requests to the gateway.
 */
export const PaymentGateway: FC = () => {
	const { tab } = useParams();
	const navigate = useNavigate();
	const roles = useSelector(getUserInfo)?.roles;
	const realmRoles = useSelector(selectRealmRoles);

	const hasAccess = useMemo(
		() => isPathAllowed(PATHS.PAYMENT_GATEWAY_PAGE, roles, realmRoles),
		[roles, realmRoles],
	);

	const value: EPgTab = tab && isPgTab(tab) ? tab : PG_DEFAULT_TAB;

	const selectTab = useCallback(
		(newValue: string) => navigate(pgTabPath(newValue), { replace: true }),
		[navigate],
	);

	// An unknown tab in the URL falls back to the default one.
	useEffect(() => {
		if (hasAccess && tab && !isPgTab(tab)) selectTab(PG_DEFAULT_TAB);
	}, [hasAccess, tab, selectTab]);

	if (!hasAccess) {
		return (
			<Box sx={{ m: '0 10px' }}>
				<Paper sx={{ p: 3, mt: 1 }}>
					<Typography variant="h6" color="primary" gutterBottom>
						Access denied
					</Typography>
					<Typography variant="body2">
						The Payment Gateway section requires the <b>paymentgateway_admin</b> role (Keycloak
						realm <b>operator</b>). Ask an administrator to grant it, then sign in again.
					</Typography>
				</Paper>
			</Box>
		);
	}

	const handleChange = (_event: SyntheticEvent, newValue: string) => selectTab(newValue);

	// Tabs collapsed into a group have no `Tab` child, so the bar must show no selection for them.
	const isGroupedTab = PG_GROUPED_TAB_VALUES.includes(value);
	const Content = TAB_CONTENT[value];

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Paper sx={{ m: '-12px -10px 10px -10px', borderRadius: '0!important' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', flexWrap: 'wrap' }}>
					<Tabs
						value={isGroupedTab ? false : value}
						onChange={handleChange}
						aria-label="Payment Gateway tabs"
						variant="scrollable"
						scrollButtons="auto"
						sx={{
							minHeight: '20px',
							'& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: '0' },
							'& .MuiButtonBase-root': { fontWeight: 'bold', color: COLORS.PRIMARY, opacity: 1 },
							'& .Mui-selected': { color: COLORS.PRIMARY, opacity: 0.5 },
						}}
					>
						{PG_PLAIN_TABS.map((t) => (
							<Tab
								key={t.value}
								value={t.value}
								label={t.label}
								sx={{ fontWeight: 'bold', p: '0 10px', minHeight: '20px' }}
							/>
						))}
					</Tabs>
					{PG_TAB_GROUPS.map((group) => (
						<TabGroupMenu
							key={group.name}
							name={group.name}
							items={group.items}
							selectedValue={value}
							onItemClick={selectTab}
						/>
					))}
					<Box sx={{ flexGrow: 1 }} />
					<Button
						size="small"
						href={PAYMENT_GATEWAY_HANGFIRE_URL}
						target="_blank"
						rel="noopener noreferrer"
						endIcon={<OpenInNew sx={{ fontSize: '0.9rem!important' }} />}
						sx={{ fontWeight: 'bold', textTransform: 'none', minHeight: '20px', p: '0 10px' }}
					>
						Jobs (Hangfire)
					</Button>
				</Box>
			</Paper>
			<PaymentViewerProvider>
				<Content />
			</PaymentViewerProvider>
		</Box>
	);
};
